import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, RefreshCw, Sparkles, TrendingUp, Zap,
  Search as SearchIcon, Image as ImageIcon,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

import { searchAnalyticsApi } from "@/lib/api";
import DateRangeFilter from "./components/DateRangeFilter";
import SummaryCards from "./components/SummaryCards";
import ChartCard from "./components/ChartCard";
import AnalyticsTable from "./components/AnalyticsTable";
import ExportCsvButton from "./components/ExportCsvButton";
import CopyIdButton from "./components/CopyIdButton";

const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("en-US") : "—");
const fmtPct = (n) => (typeof n === "number" ? `${Math.round(n * 100)}%` : "—");
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return e?.message || "حدث خطأ";
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar-SA", { timeZone: "Asia/Riyadh", hour12: false });
  } catch {
    return String(iso);
  }
};

export default function SearchAnalytics({ onBack }) {
  const [range, setRange] = useState({ range: "30d" });
  const [refresh, setRefresh] = useState(0);
  return (
    <div data-testid="search-analytics-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b-2 border-slate-900 pb-6">
        <div>
          <div className="text-sm font-bold tracking-tight text-slate-500 mb-2">
            دوائي / لوحة الإدارة
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            إحصائيات البحث عن الأدوية
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            نظرة كاملة على عمليات البحث النصي والصور والاتجاهات اليومية
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="search-analytics-refresh-btn"
            onClick={() => setRefresh((t) => t + 1)}
            className="inline-flex items-center gap-2 text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-3 rounded-xl cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            <span>تحديث</span>
          </button>
          <button
            type="button"
            data-testid="search-analytics-back-btn"
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm font-bold bg-slate-900 hover:bg-slate-800 text-white px-4 py-3 rounded-xl cursor-pointer"
          >
            <ArrowRight className="w-4 h-4" />
            <span>العودة إلى لوحة الأدمن</span>
          </button>
        </div>
      </div>

      <DateRangeFilter value={range} onChange={setRange} testidPrefix="search-range" />

      <SummarySection range={range} refresh={refresh} />
      <StatsSection range={range} refresh={refresh} />
      <LogsSection range={range} refresh={refresh} />
    </div>
  );
}

// ── Summary ────────────────────────────────────────────────────────────
function SummarySection({ range, refresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    searchAnalyticsApi
      .summary(range)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(fmtErr(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range, refresh]);

  if (loading) return <SkeletonSummary />;
  if (err)
    return (
      <div data-testid="search-summary-error" className="p-4 rounded-2xl bg-red-50 border-2 border-red-200 text-red-800 text-sm">
        {err}
      </div>
    );
  if (!data) return null;

  const cards = [
    { testid: "sa-total", label: "إجمالي عمليات البحث", value: fmtNum(data.total_searches) },
    { testid: "sa-text", label: "بحث نصي", value: fmtNum(data.text_searches) },
    { testid: "sa-ocr", label: "بحث بالصورة", value: fmtNum(data.ocr_searches) },
    { testid: "sa-matched", label: "مطابقات", value: fmtNum(data.matched), tone: "emerald" },
    { testid: "sa-no-match", label: "بدون نتيجة", value: fmtNum(data.no_match), tone: "amber" },
    { testid: "sa-failed", label: "فشل", value: fmtNum(data.failed), tone: "red" },
    { testid: "sa-success-rate", label: "معدل النجاح", value: fmtPct(data.success_rate) },
    { testid: "sa-avg-ms", label: "متوسط زمن البحث", value: `${fmtNum(data.average_latency_ms)} م.ث` },
    { testid: "sa-gemini", label: "استخدام Gemini", value: fmtNum(data.gemini_used_count) },
    { testid: "sa-deepseek", label: "استخدام DeepSeek", value: fmtNum(data.deepseek_used_count) },
  ];

  return (
    <div className="space-y-4">
      {data.analytics_collection_started_at && (
        <div
          data-testid="sa-collection-start-notice"
          className="text-xs text-slate-600 bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 flex items-start gap-2"
        >
          <Sparkles className="w-4 h-4 text-slate-500 mt-0.5" />
          <span>
            بدأ جمع تحليلات البحث من{" "}
            <b dir="ltr">{fmtDateTime(data.analytics_collection_started_at)}</b>.
            قد تظهر محاولات DeepSeek الأقدم ضمن استهلاك DeepSeek فقط.
          </span>
        </div>
      )}
      <SummaryCards cards={cards} testid="sa-summary-cards" />
      {data.top_searched_drugs?.length > 0 && (
        <ChartCard title="أكثر الأدوية بحثاً" testid="sa-top-drugs-chart">
          <div style={{ width: "100%", height: 300 }} dir="ltr">
            <ResponsiveContainer>
              <BarChart data={data.top_searched_drugs} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey={(d) => d.name || d.drug_id}
                  tick={{ fontSize: 11 }}
                  width={140}
                />
                <Tooltip />
                <Bar dataKey="count" name="مرات" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}
    </div>
  );
}

// ── Daily stats ────────────────────────────────────────────────────────
function StatsSection({ range, refresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    searchAnalyticsApi
      .stats(range)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(fmtErr(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range, refresh]);

  if (loading) return <SkeletonChart />;
  if (err)
    return (
      <div data-testid="sa-stats-error" className="p-4 rounded-2xl bg-red-50 border-2 border-red-200 text-red-800 text-sm">
        {err}
      </div>
    );
  if (!data?.daily?.length)
    return (
      <ChartCard testid="sa-stats-empty" title="الاتجاه اليومي">
        <div className="text-center py-10 text-sm text-slate-500">
          لا توجد بيانات في الفترة المختارة.
        </div>
      </ChartCard>
    );

  return (
    <>
      <ChartCard title="الاتجاه اليومي للبحث" testid="sa-daily-chart">
        <div style={{ width: "100%", height: 300 }} dir="ltr">
          <ResponsiveContainer>
            <LineChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" name="إجمالي" stroke="#0f172a" strokeWidth={2} />
              <Line type="monotone" dataKey="matched" name="مطابقات" stroke="#059669" strokeWidth={2} />
              <Line type="monotone" dataKey="no_match" name="بدون نتيجة" stroke="#f59e0b" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
      {data.top_no_result_queries?.length > 0 && (
        <ChartCard title="أعلى الاستعلامات بدون نتيجة" testid="sa-no-result-chart">
          <div style={{ width: "100%", height: 260 }} dir="ltr">
            <ResponsiveContainer>
              <BarChart data={data.top_no_result_queries} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="query" tick={{ fontSize: 11 }} width={160} />
                <Tooltip />
                <Bar dataKey="count" name="مرات" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}
    </>
  );
}

// ── Logs ───────────────────────────────────────────────────────────────
function LogsSection({ range, refresh }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const params = useMemo(() => ({ ...range, page, limit: 20 }), [range, page]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    searchAnalyticsApi
      .logs(params)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(fmtErr(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [params, refresh]);

  const columns = useMemo(
    () => [
      { key: "created_at", header: "الوقت (الرياض)", render: (r) => <span dir="ltr" className="text-xs">{fmtDateTime(r.created_at)}</span> },
      { key: "source", header: "المصدر", render: (r) => (
        <span className="inline-flex items-center gap-1 text-xs">
          {r.source === "ocr" ? <ImageIcon className="w-3 h-3" /> : <SearchIcon className="w-3 h-3" />}
          {r.source === "ocr" ? "صورة" : "نص"}
        </span>
      )},
      { key: "status", header: "الحالة", render: (r) => {
        const s = r.status;
        const tone = s === "matched" ? "bg-emerald-100 text-emerald-800"
                   : s === "no_match" ? "bg-amber-100 text-amber-800"
                   : "bg-red-100 text-red-800";
        const label = s === "matched" ? "مطابقة" : s === "no_match" ? "بدون نتيجة" : "فشل";
        return <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${tone}`}>{label}</span>;
      }},
      { key: "normalized_query", header: "الاستعلام", render: (r) => <span className="text-xs" dir="auto">{r.normalized_query || "—"}</span> },
      { key: "matched_drug_names", header: "المطابقات", render: (r) => (
        <span className="text-xs">{(r.matched_drug_names || []).join("، ") || "—"}</span>
      )},
      { key: "latency_ms", header: "الزمن (م.ث)" },
      { key: "flags", header: "AI", render: (r) => (
        <span className="inline-flex items-center gap-2 text-xs">
          {r.gemini_used && <span title="Gemini" className="inline-flex items-center gap-1"><Zap className="w-3 h-3 text-purple-600" />G</span>}
          {r.deepseek_used && <span title="DeepSeek" className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3 text-blue-600" />D</span>}
          {!r.gemini_used && !r.deepseek_used && "—"}
        </span>
      )},
      { key: "request_id", header: "Request ID", render: (r) => (
        <div className="inline-flex items-center gap-2">
          <span dir="ltr" className="text-xs text-slate-500 truncate max-w-[120px]" title={r.request_id}>
            {(r.request_id || "").slice(0, 8) || "—"}
          </span>
          {r.request_id && <CopyIdButton value={r.request_id} testid={`sa-copy-req-${r.request_id}`} />}
        </div>
      )},
    ],
    []
  );

  const onPageChange = useCallback((p) => setPage(p), []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-bold text-slate-900">سجل عمليات البحث</h3>
        <ExportCsvButton
          testid="sa-export-csv"
          href={searchAnalyticsApi.csvUrl(range)}
          label="تصدير CSV"
        />
      </div>
      <AnalyticsTable
        testid="sa-logs-table"
        columns={columns}
        rows={data?.items}
        meta={data?.meta}
        loading={loading}
        error={err}
        onPageChange={onPageChange}
        emptyText="لا توجد عمليات بحث في هذه الفترة."
      />
    </div>
  );
}

// ── Skeletons ─────────────────────────────────────────────────────────
function SkeletonSummary() {
  return (
    <div data-testid="sa-summary-loading" className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}
function SkeletonChart() {
  return (
    <div data-testid="sa-stats-loading" className="h-64 rounded-2xl bg-slate-100 animate-pulse flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );
}
